import React, { createContext, useContext } from 'react';
import { useTenantProductConfig } from '@/products/shared/hooks/useTenantProductConfig';
import type { ProductFlag } from '@/products/shared/utils/feature-access';

interface TenantProductConfigContextType {
  productConfig: Record<ProductFlag, boolean> | null;
  isLoading: boolean;
  isProductEnabled: (product: ProductFlag) => boolean;
  getEnabledProducts: () => ProductFlag[];
}

const TenantProductConfigContext = createContext<TenantProductConfigContextType | undefined>(undefined);

export function TenantProductConfigProvider({ children }: { children: React.ReactNode }) {
  const { data: productConfig, isLoading } = useTenantProductConfig();

  const isProductEnabled = (product: ProductFlag): boolean => {
    // Check tenant config first, then fallback to environment variables
    // The useTenantProductConfig hook handles the fallback logic
    if (productConfig && productConfig[product] !== undefined) {
      return productConfig[product];
    }
    
    // Not found in tenant config or env = disabled
    return false;
  };

  const getEnabledProducts = (): ProductFlag[] => {
    const products: ProductFlag[] = [
      'email-analytics',
      'email-analytics-v2',
      'penny',
      'new-payroll-testing',
      'penny2',
      'chat',
      'compliance',
      'integrations',
    ];
    return products.filter(isProductEnabled);
  };

  return (
    <TenantProductConfigContext.Provider
      value={{
        productConfig: productConfig || null,
        isLoading,
        isProductEnabled,
        getEnabledProducts,
      }}
    >
      {children}
    </TenantProductConfigContext.Provider>
  );
}

export function useTenantProductConfigContext() {
  const context = useContext(TenantProductConfigContext);
  if (context === undefined) {
    throw new Error('useTenantProductConfigContext must be used within a TenantProductConfigProvider');
  }
  return context;
}

